import React, { useState, useEffect } from "react";
import { Outlet, Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, ROLE_META } from "../lib/api";
import { AvailabilityPill } from "./AvailabilityPill";
import {
  Plus, LayoutDashboard, Users, ClipboardList, Stethoscope, FlaskConical, Pill, Banknote, LineChart, FileText, Building2, ShieldCheck, Bell, HelpCircle, LogOut, Search, Circle, Radio, MessageSquare, BedDouble, Archive, Smile, Eye, Footprints,
} from "lucide-react";

const ITEMS = [
  { to: "/app",             label: "Dashboard",   icon: LayoutDashboard, roles: "*" },
  { to: "/app/patients",    label: "Patients",    icon: Users,           roles: ["doctor","nurse","hospital_admin","cashier","radiologist","super_admin"] },
  { to: "/app/queue",       label: "Queue",       icon: ClipboardList,   roles: ["receptionist","doctor","nurse","hospital_admin","super_admin"] },
  { to: "/app/doctor",      label: "Clinical",    icon: Stethoscope,     roles: ["doctor","super_admin"] },
  { to: "/app/doctors",     label: "Doctors",     icon: Users,           roles: ["doctor","hospital_admin","super_admin"] },
  { to: "/app/admitted",    label: "Admitted",    icon: BedDouble,       roles: ["doctor","nurse","hospital_admin","super_admin"] },
  { to: "/app/discharges",  label: "Discharges",  icon: Archive,         roles: ["doctor","nurse","hospital_admin","cfo","super_admin"] },
  { to: "/app/nurse",       label: "Nursing",     icon: Stethoscope,     roles: ["nurse","super_admin"] },
  { to: "/app/lab",         label: "Laboratory",  icon: FlaskConical,    roles: ["lab_tech","doctor","super_admin","hospital_admin"] },
  { to: "/app/imaging",     label: "Imaging",     icon: Radio,           roles: ["radiologist","doctor","hospital_admin","super_admin"] },
  { to: "/app/pharmacy",    label: "Pharmacy",    icon: Pill,            roles: ["pharmacist","super_admin","hospital_admin"] },
  { to: "/app/billing",     label: "Billing",     icon: Banknote,        roles: ["receptionist","cashier","hospital_admin","cfo","super_admin"] },
  { to: "/app/finance",     label: "Finance",     icon: LineChart,       roles: ["cfo","hospital_admin","super_admin"] },
  { to: "/app/admin",       label: "Admin",       icon: Building2,       roles: ["hospital_admin","super_admin"] },
  { to: "/app/super-admin", label: "Platform",    icon: ShieldCheck,     roles: ["super_admin"] },
  { to: "/app/reports",     label: "Reports",     icon: LineChart,       roles: ["doctor","hospital_admin","cfo","records_officer","super_admin"] },
  { to: "/app/reports/hmis", label: "HMIS",        icon: FileText,        roles: ["hospital_admin","records_officer","super_admin"] },
  { to: "/app/audit",       label: "Audit",       icon: ClipboardList,   roles: ["hospital_admin","cfo","super_admin"] },
  { to: "/app/messages",    label: "Messages",    icon: MessageSquare,   roles: ["receptionist","doctor","nurse","lab_tech","radiologist","pharmacist","cashier","hospital_admin","cfo","super_admin"] },
];

// Feature modules — surfaced in the nav ONLY when the hospital's `organization.features[key]` is true.
// The super_admin toggles these per-hospital in Platform · Hospitals · Manage features.
// Keys MUST match `FEATURE_KEYS` in backend/routes/platform.py.
export const FEATURE_MODULES = [
  { key: "dentist",   to: "/app/dentist",   label: "Dentist",         icon: Smile,      roles: "*" },
  { key: "optical",   to: "/app/optical",   label: "Optical Centre",  icon: Eye,        roles: "*" },
  { key: "orthotics", to: "/app/orthotics", label: "Orthotics Clinic", icon: Footprints, roles: "*" },
];

export default function AppShell() {
  const { user, organization, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const loadBadges = () => {
    api.get("/notifications").then((r) => setNotifications(r.data || [])).catch(() => {});
    api.get("/messages/threads").then((r) => {
      const total = (r.data || []).reduce((s, t) => s + (t.unread || 0), 0);
      setUnreadMessages(total);
    }).catch(() => setUnreadMessages(0));
  };

  useEffect(() => {
    loadBadges();
    const t = setInterval(loadBadges, 4000);
    return () => clearInterval(t);
  }, [location.pathname]);

  const doSearch = async (v) => {
    setSearch(v);
    if (v.length < 2) { setSearchResults([]); return; }
    try {
      const { data } = await api.get(`/patients?q=${encodeURIComponent(v)}&limit=8`);
      setSearchResults(data || []);
    } catch { setSearchResults([]); }
  };

  const allowed = (roles) => roles === "*" || roles.includes(user?.role) || user?.role === "super_admin";
  const specialityOnly = organization?.speciality_only === true;
  // For speciality-only clinics (Optical / Dental / Orthotics) hide the general-hospital modules
  // that don't apply to their day-to-day workflow.
  const SPECIALITY_HIDDEN = new Set(["/app/admitted", "/app/discharges", "/app/nurse", "/app/doctor"]);
  const baseItems = specialityOnly ? ITEMS.filter((i) => !SPECIALITY_HIDDEN.has(i.to)) : ITEMS;
  // Merge base nav with hospital-enabled feature modules (dentist, optical, ...)
  const featureItems = FEATURE_MODULES.filter((f) => organization?.features?.[f.key]);
  const NAV_ITEMS = [...baseItems, ...featureItems];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="app-shell">
      {/* Top header — brand + org + search + user */}
      <header className="h-16 bg-white border-b border-slate-200 px-4 lg:px-6 flex items-center gap-4 flex-shrink-0">
        <Link to="/app" className="flex items-center gap-2 flex-shrink-0" data-testid="header-brand">
          <div className="w-8 h-8 rounded-md overflow-hidden shadow-sm ring-1 ring-slate-200">
            <img src="/vms-logo.png" alt="VMS" className="w-full h-full object-cover" />
          </div>
          <div className="hidden sm:block">
            <div className="text-sm font-semibold text-slate-900 leading-tight">Vitality Medical</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 leading-tight">{organization?.name || "Software"}</div>
          </div>
        </Link>

        <div className="relative flex-1 max-w-lg">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="global-search"
            value={search}
            onChange={(e) => doSearch(e.target.value)}
            placeholder="Search Patient ID, name, phone, National ID…"
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]"
          />
          {searchResults.length > 0 && (
            <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
              {searchResults.map((p) => (
                <button
                  key={p.patient_id}
                  data-testid={`search-result-${p.patient_id}`}
                  onClick={() => { setSearch(""); setSearchResults([]); navigate(`/app/patients/${p.patient_id}`); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                >
                  <div className="text-sm font-medium text-slate-800">{p.first_name} {p.last_name}</div>
                  <div className="text-xs text-slate-500">{p.ths_id} · {p.phone_primary} · {p.district || "—"}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <span className="chip chip-green hidden md:flex" data-testid="connectivity-status"><Circle className="w-2 h-2 fill-[#2E7D32] text-[#2E7D32]" /> Online</span>

          <div className="relative">
            <button data-testid="notif-btn" onClick={() => setNotifOpen((v) => !v)} className="relative w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50">
              <Bell className="w-4 h-4 text-slate-600" />
              {notifications.length > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#D32F2F] text-white text-[10px] flex items-center justify-center">{notifications.length}</span>}
            </button>
            {notifOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg border border-slate-200 shadow-lg z-30" data-testid="notif-drawer">
                <div className="p-3 border-b border-slate-200 flex items-center justify-between">
                  <div className="text-sm font-semibold">Notifications</div>
                  <div className="text-xs text-slate-500">{notifications.length} active</div>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No notifications</div>}
                  {notifications.map((n) => (
                    <button
                      key={n.notification_id}
                      data-testid={`notif-item-${n.notification_id}`}
                      onClick={() => { setNotifOpen(false); if (n.link) navigate(n.link); }}
                      className="w-full text-left p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 disabled:cursor-default"
                      disabled={!n.link}
                    >
                      <div className="flex items-start gap-2">
                        <span className={`chip ${n.severity === 'critical' ? 'chip-red' : n.severity === 'warning' ? 'chip-amber' : 'chip-blue'}`}>{n.severity || "info"}</span>
                      </div>
                      <div className="text-sm text-slate-800 mt-1.5">{n.message}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50" data-testid="help-btn"><HelpCircle className="w-4 h-4 text-slate-600" /></button>

          <AvailabilityPill />

          <div className="flex items-center gap-2 pl-2 border-l border-slate-200">
            <div className="w-8 h-8 rounded-full bg-[#e6f0fb] text-[#1565C0] flex items-center justify-center text-sm font-semibold" data-testid="user-avatar">
              {(user?.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("")}
            </div>
            <div className="hidden md:block">
              <div className="text-sm font-medium text-slate-800 leading-tight">{user?.name}</div>
              <div className="text-[11px] text-slate-500 capitalize">{ROLE_META[user?.role]?.label || user?.role}</div>
            </div>
            <button onClick={logout} data-testid="logout-btn" className="ml-2 w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50" title="Sign out"><LogOut className="w-4 h-4 text-slate-600" /></button>
          </div>
        </div>
      </header>

      {/* Horizontal nav bar */}
      <nav className="bg-[#0a1628] border-b border-slate-800 px-2 lg:px-4 flex-shrink-0" data-testid="app-nav">
        <div className="flex items-center gap-0.5 h-12 overflow-x-auto ths-nav-scroll">
          {NAV_ITEMS.filter((i) => allowed(i.roles)).map((it) => {
            const badge = it.to === "/app/messages" && unreadMessages > 0 ? unreadMessages : 0;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/app"}
                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative flex-shrink-0 h-9 px-3 rounded-lg flex items-center gap-1.5 text-[13px] font-medium transition-colors ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-slate-300 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <it.icon className="w-3.5 h-3.5" strokeWidth={2} />
                <span>{it.label}</span>
                {badge > 0 && (
                  <span data-testid="msg-badge" className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D32F2F] text-white text-[10px] font-bold flex items-center justify-center">
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Content */}
      <main className="flex-1 min-w-0 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
